import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, GraduationCap, Presentation, Copy, Mail, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Field, NativeSelect } from "./common";
import { api, formatApiError } from "../lib/api";
import { todayISO, addDays, fmtDate } from "../lib/format";
import { cn } from "../lib/utils";

const blank = {
  user_type: "formateur", nom: "", prenom: "", email: "", telephone: "", poste: "",
  matricule: "", pin: "", is_admin: false, active: true, eleve_id: "",
  access_start: "", access_end: "", badge_type: "", badge_code: "", permissions: null,
};

// Création / modification d'un accès. Le choix Formateur / Élève détermine
// les fonctions disponibles : un formateur a un accès PERMANENT et des
// permissions par fonction ; un élève a un accès TEMPORAIRE (dates) et ne
// voit que son planning et son suivi.
export function UserFormDialog({ open, onOpenChange, initial, preset, onSaved }) {
  const editing = !!initial;
  const [f, setF] = useState(blank);
  const [catalog, setCatalog] = useState([]);
  const [fiches, setFiches] = useState([]);
  const [sendCreds, setSendCreds] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCreated(null);
    setSendCreds(false);
    api.get("/admin/permissions/catalog").then(({ data }) => setCatalog(data.catalog)).catch(() => {});
    api.get("/eleves").then(({ data }) => setFiches(data)).catch(() => setFiches([]));
    if (initial) {
      setF({ ...blank, ...initial, pin: "", eleve_id: initial.eleve_id || "", access_start: initial.access_start || "", access_end: initial.access_end || "",
        badge_type: initial.badge_type || "", badge_code: initial.badge_code || "", email: initial.email || "", telephone: initial.telephone || "" });
    } else {
      const base = { ...blank, access_start: todayISO(), access_end: addDays(todayISO(), 180), ...(preset || {}) };
      setF(base);
      api.get("/users/generate-credentials").then(({ data }) => setF((x) => ({ ...x, matricule: x.matricule || data.matricule, pin: x.pin || data.pin }))).catch(() => {});
    }
  }, [open, initial, preset]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isEleve = f.user_type === "eleve";

  const chooseFiche = (id) => {
    const fiche = fiches.find((x) => x.id === id);
    setF((x) => ({
      ...x, eleve_id: id,
      ...(fiche && !editing ? { nom: fiche.nom, prenom: fiche.prenom, email: fiche.email || x.email, telephone: fiche.telephone || x.telephone,
        access_end: fiche.date_fin_formation || x.access_end } : {}),
    }));
  };

  const regen = async () => {
    const { data } = await api.get("/users/generate-credentials");
    setF((x) => ({ ...x, ...(editing ? {} : { matricule: data.matricule }), pin: data.pin }));
  };

  const perm = (mod, action) => {
    const p = f.permissions || initial?.permissions;
    if (!p) return true; // nouveau formateur : tout est accordé par défaut
    return !!p?.[mod]?.[action];
  };
  const togglePerm = (mod, action) => {
    const base = f.permissions || initial?.permissions || Object.fromEntries(catalog.map((m) => [m.id, Object.fromEntries(m.actions.map((a) => [a.id, true]))]));
    const next = JSON.parse(JSON.stringify(base));
    next[mod] = { ...(next[mod] || {}), [action]: !perm(mod, action) };
    setF({ ...f, permissions: next });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        user_type: f.user_type, nom: f.nom, prenom: f.prenom, email: f.email, telephone: f.telephone,
        matricule: f.matricule, is_admin: !isEleve && f.is_admin,
        badge_type: f.badge_code ? f.badge_type || "nfc" : null, badge_code: f.badge_code || null,
      };
      if (f.poste) payload.poste = f.poste;
      if (f.pin) payload.pin = f.pin;
      if (isEleve) {
        payload.eleve_id = f.eleve_id || null;
        payload.access_start = f.access_start || null;
        payload.access_end = f.access_end || null;
      } else if (f.permissions) {
        payload.permissions = f.permissions;
      }
      let data;
      if (editing) {
        payload.active = f.active;
        ({ data } = await api.patch(`/users/${initial.id}`, payload));
        toast.success("Accès mis à jour");
        onOpenChange(false);
      } else {
        payload.send_credentials = sendCreds;
        ({ data } = await api.post("/users", payload));
        setCreated({ ...data, pin: f.pin });
        if (data.email_status) {
          if (data.email_status.sent) toast.success("Identifiants envoyés par e-mail");
          else toast.warning(`E-mail non envoyé : ${data.email_status.error}`);
        }
      }
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const availableFiches = fiches.filter((x) => !x.has_account || x.id === initial?.eleve_id);

  if (created) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Accès créé</DialogTitle>
            <DialogDescription>Notez ces identifiants : le code PIN ne sera plus affiché.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2" data-testid="created-credentials">
            <p className="text-sm"><span className="text-muted-foreground">Utilisateur :</span> <b>{created.prenom} {created.nom}</b> ({created.user_type === "eleve" ? "élève" : "formateur"})</p>
            <p className="text-sm"><span className="text-muted-foreground">Matricule :</span> <code className="text-base font-semibold">{created.matricule}</code></p>
            <p className="text-sm"><span className="text-muted-foreground">Code PIN :</span> <code className="text-base font-semibold">{created.pin}</code></p>
            {created.user_type === "eleve" && <p className="text-sm"><span className="text-muted-foreground">Accès :</span> du {fmtDate(created.access_start)} au {fmtDate(created.access_end)}</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(`Matricule : ${created.matricule}\nCode PIN : ${created.pin}`); toast.success("Copié"); }}><Copy size={14} /> Copier</Button>
            <Button onClick={() => onOpenChange(false)}>Terminé</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Modifier l'accès — ${initial.prenom} ${initial.nom}` : "Créer un accès"}</DialogTitle>
          <DialogDescription>Connexion par matricule (7 chiffres) et code PIN (8 chiffres).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid="user-form">
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: "formateur", label: "Formateur", sub: "Accès permanent", icon: Presentation },
              { id: "eleve", label: "Élève", sub: "Accès temporaire", icon: GraduationCap },
            ].map((t) => (
              <button
                type="button"
                key={t.id}
                onClick={() => setF({ ...f, user_type: t.id })}
                data-testid={`user-type-${t.id}`}
                className={cn("flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-colors", f.user_type === t.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent/50")}
              >
                <t.icon size={22} className={f.user_type === t.id ? "text-primary" : "text-muted-foreground"} />
                <span><span className="block font-semibold text-sm">{t.label}</span><span className="block text-xs text-muted-foreground">{t.sub}</span></span>
              </button>
            ))}
          </div>

          {isEleve && (
            <Field label="Fiche élève" hint="L'accès élève est relié à une fiche : il y voit son planning et son suivi. Sans choix, une fiche est créée automatiquement.">
              <NativeSelect value={f.eleve_id} onChange={(e) => chooseFiche(e.target.value)} data-testid="user-form-fiche">
                <option value="">— Créer une fiche automatiquement —</option>
                {availableFiches.map((x) => <option key={x.id} value={x.id}>{x.nom} {x.prenom}</option>)}
              </NativeSelect>
            </Field>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nom" required><Input value={f.nom} onChange={set("nom")} required data-testid="user-form-nom" /></Field>
            <Field label="Prénom" required><Input value={f.prenom} onChange={set("prenom")} required data-testid="user-form-prenom" /></Field>
            <Field label="E-mail"><Input type="email" value={f.email} onChange={set("email")} /></Field>
            <Field label="Téléphone"><Input value={f.telephone} onChange={set("telephone")} /></Field>
          </div>

          {isEleve ? (
            <div className="grid gap-3 sm:grid-cols-2 rounded-xl border border-border p-3">
              <Field label="Début de l'accès" required><Input type="date" value={f.access_start} onChange={set("access_start")} required data-testid="user-form-access-start" /></Field>
              <Field label="Fin de l'accès" required><Input type="date" value={f.access_end} onChange={set("access_end")} required data-testid="user-form-access-end" /></Field>
              <p className="sm:col-span-2 text-xs text-muted-foreground">En dehors de cette période, l'élève ne peut plus se connecter (il est automatiquement déconnecté à l'expiration).</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border p-3 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={!!f.is_admin} onCheckedChange={(v) => setF({ ...f, is_admin: !!v })} data-testid="user-form-admin" /><ShieldCheck size={15} /> Administrateur (toutes les fonctions + Administration)</label>
              {!f.is_admin && catalog.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-3">
                  {catalog.map((m) => (
                    <div key={m.id}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{m.label}</p>
                      <div className="space-y-1">
                        {m.actions.map((a) => (
                          <label key={a.id} className="flex items-start gap-2 text-xs leading-snug">
                            <Checkbox className="mt-0.5" checked={perm(m.id, a.id)} onCheckedChange={() => togglePerm(m.id, a.id)} />
                            {a.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Matricule (7 chiffres)" required>
              <Input value={f.matricule} onChange={set("matricule")} required pattern="[0-9]{7}" inputMode="numeric" maxLength={7} className="font-mono" data-testid="user-form-matricule" />
            </Field>
            <Field label={editing ? "Nouveau code PIN (laisser vide = inchangé)" : "Code PIN (8 chiffres)"} required={!editing}>
              <div className="flex gap-2">
                <Input value={f.pin} onChange={set("pin")} required={!editing} pattern="[0-9]{8}" inputMode="numeric" maxLength={8} className="font-mono" data-testid="user-form-pin" />
                <Button type="button" variant="outline" size="icon" onClick={regen} title="Générer"><Wand2 size={15} /></Button>
              </div>
            </Field>
          </div>

          <details className="rounded-xl border border-border p-3">
            <summary className="text-sm font-medium cursor-pointer">Badge NFC / RFID (facultatif)</summary>
            <div className="grid gap-3 sm:grid-cols-[140px_1fr] mt-3">
              <Field label="Type"><NativeSelect value={f.badge_type || "nfc"} onChange={set("badge_type")}><option value="nfc">NFC</option><option value="rfid">RFID</option></NativeSelect></Field>
              <Field label="Code du badge"><Input value={f.badge_code} onChange={set("badge_code")} placeholder="Passez le badge dans ce champ" /></Field>
            </div>
          </details>

          {editing ? (
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.active !== false} onCheckedChange={(v) => setF({ ...f, active: !!v })} /> Compte actif</label>
          ) : (
            <label className={cn("flex items-center gap-2 text-sm", !f.email && "opacity-50")}>
              <Checkbox checked={sendCreds} disabled={!f.email} onCheckedChange={(v) => setSendCreds(!!v)} data-testid="user-form-send" />
              <Mail size={14} /> Envoyer les identifiants par e-mail {!f.email && "(renseignez un e-mail)"}
            </label>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="user-form-submit">{editing ? "Enregistrer" : "Créer l'accès"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
